import React from "react";
import { NavLink } from "react-router-dom";
import { useUser } from "@clerk/clerk-react";
import { useClerk } from "@clerk/shared/react";
import DarkModeToggle from "../DarkModeToggle";
import NotificationBell from "../NotificationBell";
import { useUserPersona } from "../../hooks/useUserPersona";
import type { PersonaId } from "../../utils/personas";

interface SidebarProps {
  onNavigate?: () => void;
}

interface NavItem {
  to: string;
  label: string;
  icon: React.ReactNode;
  // If set, only show when user persona is in this list. Undefined = show to all.
  personas?: PersonaId[];
  // If true, only show when the venue probe came back positive.
  requiresVenue?: boolean;
}

const ICON: Record<string, React.ReactNode> = {
  card: (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
    </svg>
  ),
  leads: (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
    </svg>
  ),
  insights: (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
    </svg>
  ),
  signature: (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
    </svg>
  ),
  venue: (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
    </svg>
  ),
  help: (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  ),
  store: (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
    </svg>
  ),
  admin: (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.066 2.573c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.573 1.066c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.066-2.573c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
    </svg>
  ),
  profile: (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
    </svg>
  ),
};

const PRIMARY_NAV: NavItem[] = [
  { to: "/my-card", label: "My Card", icon: ICON.card },
  {
    to: "/leads",
    label: "Leads",
    icon: ICON.leads,
    personas: ["recruiter", "sales", "real-estate", "consultant", "executive", "other"],
  },
  { to: "/insights", label: "Insights", icon: ICON.insights },
  { to: "/email-signature", label: "Email Signature", icon: ICON.signature },
  {
    to: "/venue/setup",
    label: "Venue",
    icon: ICON.venue,
    personas: ["hospitality"],
    requiresVenue: false,
  },
];

const ACCOUNT_NAV: NavItem[] = [
  { to: "/profile", label: "Profile", icon: ICON.profile },
  { to: "/help", label: "Help", icon: ICON.help },
  { to: "/pricing", label: "Store", icon: ICON.store },
];

const ADMIN_EMAILS = ["sean@whobrew.us", "seanthomas2002@gmail.com"];

const Sidebar: React.FC<SidebarProps> = ({ onNavigate }) => {
  const { user } = useUser();
  const { signOut } = useClerk();
  const { persona, hasVenue } = useUserPersona();
  const isAdmin = ADMIN_EMAILS.includes(user?.primaryEmailAddress?.emailAddress || "");

  const linkClass = ({ isActive }: { isActive: boolean }) =>
    `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors relative ${
      isActive
        ? "bg-coffee-800 text-white border-l-2 border-coffee-500 pl-[10px]"
        : "text-coffee-300 hover:text-white hover:bg-coffee-800"
    }`;

  // Decide whether to show a nav item given the user's persona + venue state.
  // Venue-tagged routes appear if persona matches OR the venue probe found
  // an existing venue (so legacy users keep access).
  const visible = (item: NavItem): boolean => {
    if (item.to === "/venue/setup") {
      if (hasVenue) return true;
      return !!(item.personas && persona && item.personas.includes(persona));
    }
    if (!item.personas) return true;
    if (!persona) return true; // user hasn't onboarded yet — show defaults
    return item.personas.includes(persona);
  };

  const primaryItems = PRIMARY_NAV.filter(visible);
  const accountItems = ACCOUNT_NAV.filter(visible);

  // Derive initials for avatar chip
  const initials = user?.firstName && user?.lastName
    ? `${user.firstName[0]}${user.lastName[0]}`.toUpperCase()
    : user?.firstName
    ? user.firstName[0].toUpperCase()
    : "?";

  const handle = user?.primaryEmailAddress?.emailAddress?.split("@")[0] || "";

  return (
    <div className="flex flex-col h-full bg-coffee-900 text-white overflow-y-auto">
      {/* Brand wordmark */}
      <div className="px-4 pt-5 pb-4 border-b border-coffee-800">
        <span className="text-display text-lg text-white">WhoBrew</span>
      </div>

      {/* Avatar / user chip */}
      <NavLink
        to="/profile"
        onClick={onNavigate}
        className="flex items-center gap-3 px-4 py-4 border-b border-coffee-800 hover:bg-coffee-800 transition-colors"
      >
        <div className="w-9 h-9 rounded-full overflow-hidden ring-2 ring-coffee-600 flex-shrink-0">
          {user?.imageUrl ? (
            <img src={user.imageUrl} alt="Profile" className="w-full h-full object-cover" />
          ) : (
            <div className="w-full h-full bg-coffee-600 flex items-center justify-center text-white text-sm font-bold">
              {initials}
            </div>
          )}
        </div>
        <div className="min-w-0">
          <p className="text-sm font-semibold text-white truncate">
            {user?.firstName} {user?.lastName}
          </p>
          <p className="text-[11px] font-mono text-coffee-400 truncate">
            @{handle}
          </p>
        </div>
      </NavLink>

      {/* Primary nav */}
      <div className="flex-1 px-3 pt-4 pb-2 space-y-0.5">
        <div className="eyebrow text-coffee-500 px-3 mb-2">Tools</div>
        {primaryItems.map((item) => (
          <NavLink key={item.to} to={item.to} onClick={onNavigate} className={linkClass}>
            {item.icon}
            {item.label}
          </NavLink>
        ))}
      </div>

      {/* Account + secondary nav */}
      <div className="px-3 pt-2 pb-2 space-y-0.5">
        <div className="eyebrow text-coffee-500 px-3 mb-2">Account</div>
        {accountItems.map((item) => (
          <NavLink key={item.to} to={item.to} onClick={onNavigate} className={linkClass}>
            {item.icon}
            {item.label}
          </NavLink>
        ))}
        {isAdmin && (
          <>
            <div className="eyebrow text-coffee-500 px-3 mb-2 mt-3">Admin</div>
            <NavLink to="/admin" onClick={onNavigate} className={linkClass}>
              {ICON.admin}
              Admin
            </NavLink>
          </>
        )}
      </div>

      {/* Bottom utility bar */}
      <div className="px-3 py-3 border-t border-coffee-800">
        <div className="flex items-center justify-between px-3 py-2">
          <div className="flex items-center gap-2">
            <DarkModeToggle />
            <NotificationBell />
          </div>
          <button
            onClick={() => signOut()}
            className="text-xs text-coffee-400 hover:text-red-400 transition-colors font-medium"
          >
            Sign out
          </button>
        </div>
      </div>
    </div>
  );
};

export default Sidebar;
